import { useEffect, useState } from 'react';

import { EmailCredentialsBannerPure } from '@poseidon/components/EmailCredentialsBannerPure';

import { useTestEmailCredentials } from '@/hooks/trpc/useUserMutation';
import { api } from '@/utils/api';

/**
 * Banner displayed when email credentials have authentication errors.
 * Shows a retry button and link to settings page.
 * Polls every 60 seconds to check if the issue is resolved.
 */
export const EmailCredentialsBanner = () => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Return null on initial render to match server-side render and prevent hydration mismatch
  if (!isMounted) {
    return null;
  }

  return <EmailCredentialsBannerClient />;
};

/**
 * Client-only container that handles status polling + the retry mutation, then
 * delegates rendering to the pure {@link EmailCredentialsBannerPure}.
 */
const EmailCredentialsBannerClient = () => {
  const { data, isLoading, isError } = api.team.emailCredentials.useQuery(
    {},
    {
      refetchInterval: 60_000, // Check every 60 seconds
      staleTime: 30_000, // Consider data stale after 30 seconds
    },
  );

  const testCredentialMutation = useTestEmailCredentials();

  // Don't show banner if loading, errored (fail gracefully), or no data yet.
  if (isLoading || isError || !data) {
    return null;
  }

  const failedCredentials = data.emailCredentials.filter((c) => c.lastSyncError != null);
  const firstFailed = failedCredentials[0];
  if (!firstFailed) {
    return null;
  }

  return (
    <EmailCredentialsBannerPure
      failedCredentials={failedCredentials}
      isRetrying={testCredentialMutation.isPending}
      onRetry={() => testCredentialMutation.mutate({ id: firstFailed.id })}
    />
  );
};
